import {
  Authenticated,
  CustomRoutes,
  localStorageStore,
  Resource,
  type AuthProvider,
  type DataProvider,
} from "ra-core";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, Suspense, lazy, useState } from "react";
import { Route, Navigate } from "react-router";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { ContatoPage } from "../marketing/ContatoPage";
import { SimuladorPage } from "../marketing/SimuladorPage";
import { ContratarPage } from "../contratacao/ContratarPage";
import { EstudoPublicoPage } from "../consortium-groups/EstudoPublicoPage";
import { FundoMarca } from "../misc/FundoMarca";

import contacts from "../contacts";
import clients from "../clients";
import consortiumGroups from "../consortium-groups";
import { InadimplentesPage, ProposalSimulationsPage } from "../consortium";
import { CanopusPage } from "../canopus/CanopusPage";
import { CanopusInicio } from "../canopus/CanopusInicio";
import { CanopusCarteiraPage, CanopusClientesPage, CanopusComissoesPage, CanopusVendasPage } from "../canopus/CanopusListas";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { PortaoManutencao } from "../layout/Manutencao";
import { SidebarProvider } from "../layout/SidebarContext";
import { SignupPage } from "../login/SignupPage";
import vendasTransactions from "../vendas";
import { UserManagement } from "../users/UserManagement";
import {
  authProvider as defaultAuthProvider,
  dataProvider as defaultDataProvider,
} from "../providers/supabase";
import users from "../users";
import { SettingsPage } from "../settings/SettingsPage";
import type { ConfigurationContextValue } from "./ConfigurationContext";
import { ConfigurationProvider } from "./ConfigurationContext";
import {
  defaultCompanySectors,
  defaultContactGender,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider } from "./i18nProvider";
import { importarComponente } from "@/components/app/staleChunkRecovery";
import { StartPage } from "../login/StartPage.tsx";
import { AIPage } from "../ai/AIPage";
import { MeuAgentePage } from "../agents/MeuAgentePage";
import { HomeDashboard } from "../dashboard/HomeDashboard";
import { SegurosHome } from "../seguros/SegurosHome";
import { useHeartbeat } from "../commons/useHeartbeat";
import { PoliticaPrivacidadePage, SobrePage, TermosDeUsoPage } from "../institucional/PaginasLegais";

// Lazy load simulator and other non-critical routes
const ConsortiumSimulator = lazy(() => importarComponente(() => import("../consortium-groups/Simulator"), "ConsortiumSimulator"));

const CampanhasPage = lazy(() => importarComponente(() => import("../campanhas/CampanhasPage"), "CampanhasPage"));

const DesafioPessoalPage = lazy(() => importarComponente(() => import("../campanhas/DesafioPessoalPage"), "DesafioPessoalPage"));

const DesafioTimePage = lazy(() => importarComponente(() => import("../campanhas/DesafioTimePage"), "DesafioTimePage"));

const CampanhasTimePage = lazy(() => importarComponente(() => import("../campanhas/CampanhasTimePage"), "CampanhasTimePage"));

const CampanhaDetailPage = lazy(() => importarComponente(() => import("../campanhas/CampanhaDetailPage"), "CampanhaDetailPage"));

const ContratacoesPage = lazy(() => importarComponente(() => import("../contratacao/ContratacoesPage"), "ContratacoesPage"));
const ComissoesPage = lazy(() => importarComponente(() => import("../comissoes/ComissoesPage"), "ComissoesPage"));

const FluxoCaixaPage = lazy(() => importarComponente(() => import("../fluxo-caixa/FluxoCaixaPage"), "FluxoCaixaPage"));
const PrevisaoServopaPage = lazy(() => importarComponente(() => import("../previsao-servopa/PrevisaoServopaPage"), "PrevisaoServopaPage"));

const VendaShowPage = lazy(() => importarComponente(() => import("../vendas/VendaShowPage"), "VendaShowPage"));

const LancesPage = lazy(() => importarComponente(() => import("../lances/LancesPage"), "LancesPage"));
const MotorPage = lazy(() => importarComponente(() => import("../lances/MotorPage"), "MotorPage"));

const CancelamentosPage = lazy(() => importarComponente(() => import("../auditoria/CancelamentosPage"), "CancelamentosPage"));

// Seguros — carregadas sob demanda: quem está no consórcio nunca baixa esse código.
const ApolicesPage = lazy(() => importarComponente(() => import("../seguros/ApolicesPage"), "ApolicesPage"));
const PropostasSegurosPage = lazy(() => importarComponente(() => import("../seguros/PropostasPage"), "PropostasSegurosPage"));
const CobrancaPage = lazy(() => importarComponente(() => import("../seguros/CobrancaPage"), "CobrancaPage"));
const InadimplenciaPage = lazy(() => importarComponente(() => import("../seguros/InadimplenciaPage"), "InadimplenciaPage"));
const TakeRatesPage = lazy(() => importarComponente(() => import("../seguros/TakeRatesPage"), "TakeRatesPage"));
const AzosPage = lazy(() => importarComponente(() => import("../seguros/AzosPage"), "AzosPage"));
const RecebimentosPage = lazy(() => importarComponente(() => import("../seguros/RecebimentosPage"), "RecebimentosPage"));
const ExigenciasPage = lazy(() => importarComponente(() => import("../seguros/ExigenciasPage"), "ExigenciasPage"));
const CorretorasPage = lazy(() => importarComponente(() => import("../seguros/CorretorasPage"), "CorretorasPage"));
const PersistenciaPage = lazy(() => importarComponente(() => import("../seguros/PersistenciaPage"), "PersistenciaPage"));
const CotacaoPage = lazy(() => importarComponente(() => import("../seguros/CotacaoPage"), "CotacaoPage"));
const HubPage = lazy(() => importarComponente(() => import("../hub/HubPage"), "HubPage"));
const InsightsPage = lazy(() => importarComponente(() => import("../insights/InsightsPage"), "InsightsPage"));
const CotacoesPage = lazy(() => importarComponente(() => import("../demais-ramos/CotacoesPage"), "CotacoesPage"));
const ContatosDemaisRamosPage = lazy(() => importarComponente(() => import("../demais-ramos/ContatosDemaisRamosPage"), "ContatosDemaisRamosPage"));
const ContactShow = lazy(() => importarComponente(() => import("../contacts/ContactShow"), "ContactShow"));
const GroupShowPage = lazy(() => importarComponente(() => import("../consortium-groups/GroupShowPage"), "GroupShowPage"));
const AcessoRestritoPage = lazy(() => importarComponente(() => import("../admin/AcessoRestritoPage"), "AcessoRestritoPage"));
const UnidadesPage = lazy(() => importarComponente(() => import("../unidades/UnidadesPage"), "UnidadesPage"));
const ConfiguracoesPage = lazy(() => importarComponente(() => import("../admin/ConfiguracoesPage"), "ConfiguracoesPage"));
const PerformancePage = lazy(() => importarComponente(() => import("../admin/performance/PerformancePage"), "PerformancePage"));
const AuditoriaPage = lazy(() => importarComponente(() => import("../auditoria/AuditoriaPage"), "AuditoriaPage"));
const DuplicidadesPage = lazy(() => importarComponente(() => import("../admin/DuplicidadesPage"), "DuplicidadesPage"));
const OrganizacoesPage = lazy(() => importarComponente(() => import("../organizacoes/OrganizacoesPage"), "OrganizacoesPage"));
const ChavesApiPage = lazy(() => importarComponente(() => import("../organizacoes/ChavesApiPage"), "ChavesApiPage"));
const FeedPage = lazy(() => importarComponente(() => import("../feed/FeedPage"), "FeedPage"));
const FeedDoProduto = lazy(() => importarComponente(() => import("../feed/FeedPage"), "FeedDoProduto"));
const FeedConsorcio = lazy(() => importarComponente(() => import("../feed/FeedConsorcio"), "FeedConsorcio"));
const MensagensPage = lazy(() => importarComponente(() => import("../mensagens/MensagensPage"), "MensagensPage"));

// O dashboard do react-admin é o do CONSÓRCIO. O hub e o início de Seguros são
// rotas próprias (/ e /seguros) — antes isso era decidido pelo hostname, no
// momento do import, e agora é rota.

export type CRMProps = {
  dataProvider?: DataProvider;
  authProvider?: AuthProvider;
  disableTelemetry?: boolean;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * wraps the application with a `ConfigurationProvider` to provide configuration values via context.
 *
 * @param {Array<ContactGender>} contactGender - The gender options for contacts used in the application.
 * @param {string[]} companySectors - The list of company sectors used in the application.
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {string[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the CRM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {string[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         logo="/path/to/logo.png"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  contactGender = defaultContactGender,
  companySectors = defaultCompanySectors,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProvider,
  authProvider = defaultAuthProvider,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  const [queryClient] = useState(
    () => new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    }),
  );

  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // No topo do componente, e não dentro do Layout: o Hub é
  // noLayout (fora do Layout do react-admin) e ficaria sem heartbeat se ele
  // morasse lá.
  useHeartbeat();

  return (
    <ConfigurationProvider
      contactGender={contactGender}
      companySectors={companySectors}
      dealCategories={dealCategories}
      dealPipelineStatuses={dealPipelineStatuses}
      dealStages={dealStages}
      darkModeLogo={darkModeLogo}
      lightModeLogo={lightModeLogo}
      noteStatuses={noteStatuses}
      taskTypes={taskTypes}
      title={title}
    >
      <PortaoManutencao>
      <SidebarProvider>
          <Admin
          dataProvider={dataProvider}
          authProvider={authProvider}
          store={localStorageStore(undefined, "CRM")}
          layout={Layout}
          loginPage={StartPage}
          i18nProvider={i18nProvider}
          queryClient={queryClient}
          requireAuth
          disableTelemetry
          {...rest}
        >
        {/* ATENÇÃO — `noLayout` no ra-core não quer dizer só "sem sidebar":
            quer dizer FORA DO GATE DE AUTENTICAÇÃO. O CoreAdminRoutes diz isso
            em comentário no próprio código ("custom routes with no layout are
            always rendered, regardless of the auth status") e monta estas rotas
            nos três ramos, inclusive no de não-autenticado — onde o
            <LogoutOnMount>, que é quem manda pro login, fica no catch-all `*`
            que uma rota declarada aqui nunca alcança.

            Na prática: quem chegava em `/` sem sessão via o hub com "Olá" sem
            nome e "Nada pendente nos seus produtos agora" — as consultas
            voltavam não-autorizadas e o `?? []` transformava erro em lista
            vazia. Só saía clicando em "Sair" e logando.

            Por isso toda rota daqui que NÃO for pública vai embrulhada em
            <Authenticated>: é ele que redireciona pro login em vez de renderizar.
            As públicas abaixo (sign-up, set-password, forgot-password, contato, simule, estudo, privacidade, termos)
            continuam cruas de propósito — é para elas que o `noLayout` existe. */}
        <CustomRoutes noLayout>
          {/* O hub é tela cheia, sem sidebar: é a porta de entrada, não uma
              página de produto. Por isso vai em noLayout e não no `dashboard`,
              que renderia dentro do Layout do react-admin.

              `loading` é o mesmo fundo da própria página: o <Authenticated>
              renderiza null enquanto o checkAuth não responde, e como o login
              faz window.location.replace a chegada aqui é sempre load frio —
              sem isso, a continuidade do logo viraria um piscar de branco. */}
          <Route
            path="/"
            element={
              <Authenticated loading={<FundoMarca>{null}</FundoMarca>}>
                <HubPage />
              </Authenticated>
            }
          />
          {/* /insights era a casa do North Insights, que agora mora dentro do
              Acesso Restrito (/admin/insights). Link velho não quebra. */}
          <Route path="/insights" element={<Navigate to="/admin/insights" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={ContatoPage.path} element={<ContatoPage />} />
          <Route path={SimuladorPage.path} element={<SimuladorPage />} />
          <Route path={ContratarPage.path} element={<ContratarPage />} />
          <Route path={EstudoPublicoPage.path} element={<EstudoPublicoPage />} />
          {/* Exigidas pela verificação do app Google (migration 302). */}
          <Route path={SobrePage.path} element={<SobrePage />} />
          <Route path={PoliticaPrivacidadePage.path} element={<PoliticaPrivacidadePage />} />
          <Route path={TermosDeUsoPage.path} element={<TermosDeUsoPage />} />
        </CustomRoutes>

        <CustomRoutes>
          {/* Raiz de cada produto = o Feed North (30/09/2026), com a saudação
              e — no Consórcio — o resumo do dia. Os números moram no
              Dashboard de cada um (PRODUCT_MODULES[x].dashboard). */}
          <Route
            path="/consorcio"
            element={
              <Suspense fallback={<div className="p-8">Carregando o feed...</div>}>
                <FeedConsorcio />
              </Suspense>
            }
          />
          <Route path="/consorcio/dashboard" element={<HomeDashboard />} />
          <Route
            path="/seguros"
            element={
              <Suspense fallback={<div className="p-8">Carregando o feed...</div>}>
                <FeedPage />
              </Suspense>
            }
          />
          <Route path="/seguros/dashboard" element={<SegurosHome />} />
          <Route path="/seguros/apolices" element={<ApolicesPage />} />
          <Route path="/seguros/propostas" element={<PropostasSegurosPage />} />
          <Route path="/seguros/cobranca" element={<CobrancaPage />} />
          <Route path="/seguros/inadimplencia" element={<InadimplenciaPage />} />
          <Route path="/seguros/take-rates" element={<TakeRatesPage />} />
          <Route path="/seguros/azos" element={<AzosPage />} />
          <Route path="/seguros/portal-azos" element={<Navigate to="/seguros/azos" replace />} />
          <Route path="/seguros/recebimentos" element={<RecebimentosPage />} />
          <Route path="/seguros/exigencias" element={<ExigenciasPage />} />
          <Route path="/seguros/corretoras" element={<CorretorasPage />} />
          <Route path="/seguros/persistencia" element={<PersistenciaPage />} />
          <Route path="/seguros/cotacao" element={<CotacaoPage />} />
          {/* Mesma página, produto diferente: o filtro de templates sai da rota. */}
          <Route path="/seguros/meu-agente" element={<MeuAgentePage />} />
          <Route path="/inadimplentes" element={<InadimplentesPage />} />
          <Route path="/propostas" element={<ProposalSimulationsPage />} />
          {/* Consórcio Canopus (em teste, só super admin — RLS da 245). */}
          <Route path="/canopus" element={<CanopusInicio />} />
          <Route path="/canopus/planos" element={<CanopusPage />} />
          <Route path="/canopus/vendas" element={<CanopusVendasPage />} />
          <Route path="/canopus/clientes" element={<CanopusClientesPage />} />
          <Route path="/canopus/comissoes" element={<CanopusComissoesPage />} />
          <Route path="/canopus/carteira" element={<CanopusCarteiraPage />} />
          <Route path="/ai" element={<AIPage />} />
          <Route path="/meu-agente" element={<MeuAgentePage />} />
          <Route
            path="/campanhas"
            element={
              <Suspense fallback={<div className="p-8">Carregando campanhas...</div>}>
                <CampanhasPage />
              </Suspense>
            }
          />
          {/* Antes de :slug — "desafio-pessoal" não é uma campanha do banco. */}
          <Route
            path="/campanhas/resumo/desafio-pessoal"
            element={
              <Suspense fallback={<div className="p-8">Carregando resumo do time...</div>}>
                <DesafioTimePage />
              </Suspense>
            }
          />
          <Route
            path="/campanhas/resumo/campanhas"
            element={
              <Suspense fallback={<div className="p-8">Carregando resumo do time...</div>}>
                <CampanhasTimePage />
              </Suspense>
            }
          />
          <Route
            path="/campanhas/desafio-pessoal"
            element={
              <Suspense fallback={<div className="p-8">Carregando desafio...</div>}>
                <DesafioPessoalPage />
              </Suspense>
            }
          />
          <Route
            path="/campanhas/:slug"
            element={
              <Suspense fallback={<div className="p-8">Carregando campanha...</div>}>
                <CampanhaDetailPage />
              </Suspense>
            }
          />
          <Route
            path="/comissoes"
            element={
              <Suspense fallback={<div className="p-8">Carregando comissões...</div>}>
                <ComissoesPage />
              </Suspense>
            }
          />
          {/* A auditoria mudou de casa (agora é uma aba do North Insights).
              O caminho antigo vira redirect em vez de sumir: há link salvo e
              histórico de navegador apontando pra cá. */}
          <Route
            path="/auditoria"
            element={<Navigate to="/admin/auditoria" replace />}
          />
          <Route
            path="/cancelamentos"
            element={
              <Suspense fallback={<div className="p-8">Carregando cancelamentos...</div>}>
                <CancelamentosPage />
              </Suspense>
            }
          />
          <Route
            path="/vendas/:id"
            element={
              <Suspense fallback={<div className="p-8">Carregando venda...</div>}>
                <VendaShowPage />
              </Suspense>
            }
          />
          <Route
            path="/lances"
            element={
              <Suspense fallback={<div className="p-8">Carregando lances...</div>}>
                <LancesPage />
              </Suspense>
            }
          />
          {/* Pendurada em Contemplação de propósito, e não como entrada nova
              no menu: quem pergunta "posso confiar nisso?" é quem está
              olhando o plano de uma cota. A barra segue com Contemplação
              aceso (rotaAcesa escolhe o casamento mais específico). */}
          <Route
            path="/lances/motor"
            element={
              <Suspense fallback={<div className="p-8">Carregando motor...</div>}>
                <MotorPage />
              </Suspense>
            }
          />
          <Route path="/leads" element={<Navigate to="/contacts" replace />} />
          <Route
            path="/simulator"
            element={
              <Suspense fallback={<div className="p-8">Carregando simulador...</div>}>
                <ConsortiumSimulator />
              </Suspense>
            }
          />
          {/* Detalhe de um grupo, com URL própria — antes só abria como modal
              sem link possível de fora (Vendas, Lances, Comissões, ...). */}
          <Route
            path="/consortium_groups/:groupNumber"
            element={
              <Suspense fallback={<div className="p-8">Carregando grupo...</div>}>
                <GroupShowPage />
              </Suspense>
            }
          />
          <Route path="/users" element={<UserManagement />} />
          {/* Demais Produtos e Acesso Restrito ficam DENTRO do Layout (com barra
              lateral), como o resto do app. Ficavam em tela cheia sem menu
              nenhum, com um link "voltar" solto no topo — quem entrava perdia
              as rotas comuns (Contatos) e o caminho de volta padrão. O menu
              que aparece nelas é reduzido: ver SECOES_SEM_PRODUTO no
              Sidebar. */}
          <Route
            path="/demais-ramos"
            element={
              <Suspense fallback={<div className="p-8">Carregando cotações...</div>}>
                <CotacoesPage />
              </Suspense>
            }
          />
          <Route
            path="/demais-ramos/contatos"
            element={
              <Suspense fallback={<div className="p-8">Carregando contatos...</div>}>
                <ContatosDemaisRamosPage />
              </Suspense>
            }
          />
          {/* A MESMA ficha de contato do resto do app, montada aqui dentro de
              propósito: em `/contacts/:id/show` (rota compartilhada) a barra
              lateral vira a do Consórcio, e abrir um contato de Demais Produtos
              jogava a pessoa pra fora da seção sem ela ter pedido. Sair pro
              Consórcio/Seguros só quando ela CLICA numa venda específica, de
              dentro da ficha. `ShowBase` lê o id da rota, então o mesmo
              componente serve nas duas. */}
          <Route
            path="/demais-ramos/contatos/:id"
            element={
              <Suspense fallback={<div className="p-8">Carregando contato...</div>}>
                <ContactShow />
              </Suspense>
            }
          />
          {/* /feed é o endereço antigo (sino, links velhos): leva ao Feed do
              produto em que a pessoa está, que agora é a raiz dele. */}
          <Route
            path="/feed"
            element={
              <Suspense fallback={null}>
                <FeedDoProduto />
              </Suspense>
            }
          />
          <Route
            path="/demais-ramos/feed"
            element={
              <Suspense fallback={<div className="p-8">Carregando o feed...</div>}>
                <FeedPage />
              </Suspense>
            }
          />
          {/* Mensagens (migration 250): rota compartilhada, como /contacts; a de
              Demais Produtos existe para o menu da seção não trocar. */}
          {["/mensagens", "/demais-ramos/mensagens"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={<div className="p-8">Carregando as mensagens...</div>}>
                  <MensagensPage />
                </Suspense>
              }
            />
          ))}
          <Route path="/admin" element={<AcessoRestritoPage />} />
          <Route path="/admin/unidades" element={<UnidadesPage />} />
          <Route path="/admin/configuracoes" element={<ConfiguracoesPage />} />
          <Route path="/admin/performance" element={<PerformancePage />} />
          {/* /fluxo-caixa: o mesmo caixa no menu do consórcio, para a conta demo
              (migration 286) — a RLS entrega a cada um só os fluxos dele. */}
          {["/admin/fluxo-caixa", "/fluxo-caixa"].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={<div className="p-8">Carregando fluxo de caixa...</div>}>
                  <FluxoCaixaPage />
                </Suspense>
              }
            />
          ))}
          <Route
            path="/admin/previsao-servopa"
            element={
              <Suspense fallback={<div className="p-8">Carregando previsão Servopa...</div>}>
                <PrevisaoServopaPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/auditoria"
            element={
              <Suspense fallback={<div className="p-8">Carregando auditoria...</div>}>
                <div className="mx-auto w-full max-w-[1400px]">
                  <AuditoriaPage />
                </div>
              </Suspense>
            }
          />
          <Route
            path="/admin/contratacoes"
            element={
              <Suspense fallback={<div className="p-8">Carregando contratações...</div>}>
                <ContratacoesPage />
              </Suspense>
            }
          />
          <Route
            path="/indicacoes"
            element={
              <Suspense fallback={<div className="p-8">Carregando indicações...</div>}>
                <ContratacoesPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/organizacoes"
            element={
              <Suspense fallback={<div className="p-8">Carregando organizações...</div>}>
                <OrganizacoesPage />
              </Suspense>
            }
          />
          <Route
            path="/chaves-api"
            element={
              <Suspense fallback={<div className="p-8">Carregando chaves de API...</div>}>
                <ChavesApiPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/duplicidades"
            element={
              <Suspense fallback={<div className="p-8">Carregando duplicidades...</div>}>
                <DuplicidadesPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/insights"
            element={
              <Suspense fallback={<div className="p-8">Carregando insights...</div>}>
                <InsightsPage />
              </Suspense>
            }
          />
          <Route path={SettingsPage.path} element={<SettingsPage />} />
          <Route path="/funnel/*" element={<Navigate to="/deals" replace />} />
          {/* O nome que a casa usa é CRM; a rota do recurso continua /deals
              porque é o nome da tabela. Este atalho existe para quem digita ou
              compartilha o endereço que faz sentido para uma pessoa. */}
          <Route path="/crm/*" element={<Navigate to="/deals" replace />} />
        </CustomRoutes>
        <Resource name="deals" {...deals} options={{ label: 'CRM' }} />
        <Resource name="contacts" {...contacts} />
        <Resource name="clients" {...clients} options={{ label: 'Clientes' }} />
        <Resource name="contactNotes" />
        <Resource name="dealNotes" />
        <Resource name="tasks" />
        <Resource name="users" {...users} options={{ label: "Users" }} />
        <Resource name="vendas" {...vendasTransactions} options={{ label: 'Vendas' }} />
        <Resource name="consortium_groups" {...consortiumGroups} options={{ label: 'Grupos' }} />
        <Resource name="proposal_simulations" />
        <Resource name="tags" />
      </Admin>
      </SidebarProvider>
      </PortaoManutencao>
    </ConfigurationProvider>
  );
};
